import axios, { AxiosResponse } from 'axios';
import { authFetch } from './fetch';

// Interfaces for the ElasticSearch admin
export interface CdcSyncStatus {
    isRunning: boolean;
    startTime: string | null;
    message: string;
    environment?: string;
    isProduction?: boolean;
  }
  
  export interface CdcSyncTableStats {
    tableName: string;
    lastSyncedAt: string;
    lastLsn: string | null;
    processedCount: number;
  }
  
  export interface ElasticStatusResponse {
    cdcService: CdcSyncStatus;
    lastSyncStats: CdcSyncTableStats[];
    debugLoggingEnabled: boolean;
  }
  
  export type ServiceState = 'Running' | 'Stopped' | 'Failed';
  
  export interface ServiceControlResult {
    success: boolean;
    message: string;
    currentState: ServiceState;
  }

const baseUrl = '/api/Admin/Elastic';

export const ElasticSearchAPI = {
  async getStatus(): Promise<ElasticStatusResponse> {
    try {
      const response: AxiosResponse<ElasticStatusResponse> = await authFetch.get(`${baseUrl}/status`);
      if (!(response.status >= 200 && response.status < 300)) {
        throw Error(response.statusText);
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw Error(error?.response?.data?.message || 'Failed to fetch status');
      }
      throw error;
    }
  },

  async startCdcSync(): Promise<ServiceControlResult> {
    try {
      const response: AxiosResponse<ServiceControlResult> = await authFetch.post(`${baseUrl}/cdc/start`, null);
      if (!(response.status >= 200 && response.status < 300)) {
        throw Error(response.statusText);
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw Error(error?.response?.data?.message || 'Failed to start CDC sync service');
      }
      throw error;
    }
  },

  async stopCdcSync(): Promise<ServiceControlResult> {
    try {
      const response: AxiosResponse<ServiceControlResult> = await authFetch.post(`${baseUrl}/cdc/stop`, null);
      if (!(response.status >= 200 && response.status < 300)) {
        throw Error(response.statusText);
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw Error(error?.response?.data?.message || 'Failed to stop CDC sync service');
      }
      throw error;
    }
  },

  async reindexAll(batchSize: number = 2000): Promise<void> {
    try {
      const response = await authFetch.post(`${baseUrl}/index/all?batchSize=${batchSize}`, batchSize);
      if (!(response.status >= 200 && response.status < 300)) {
        throw Error(response.statusText);
      }
      return;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw Error(error?.response?.data?.message || 'Failed to start full reindex');
      }
      throw error;
    }
  },

  async indexEntityType(entityType: string, batchSize: number = 2000): Promise<void> {
    try {
        var payload = {
            'entityType': entityType,
            'batchSize': batchSize
        }
      const response = await authFetch.post(`${baseUrl}/index/${entityType}?batchSize=${batchSize}`, payload);
      if (!(response.status >= 200 && response.status < 300)) {
        throw Error(response.statusText);
      }
      return;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw Error(error?.response?.data?.message || `Failed to start ${entityType} indexing`);
      }
      throw error;
    }
  },
  async setDebugMode(enabled: boolean): Promise<void> {
    try {
      const response: AxiosResponse<void> = await authFetch.post(`${baseUrl}/debug-mode`, { enabled });
      if (!(response.status >= 200 && response.status < 300)) {
        throw Error(response.statusText);
      }
      return;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw Error(error?.response?.data?.message || 'Failed to set debug mode');
      }
      throw error;
    }
  }
};